import { createContext, type JSX, type PropsWithChildren, useContext, useMemo } from 'react';

import { loadEnv } from './loadEnv';
import { type Env } from './types';

export const EnvProviderContext = createContext<Env | undefined>(undefined);

export interface EnvProviderProps {
  env?: Env;
}

export const EnvProvider = ({ children, env }: PropsWithChildren<EnvProviderProps>): JSX.Element => {
  const value = useMemo(() => {
    if (env) return env;
    return loadEnv();
  }, [env]);

  return <EnvProviderContext.Provider value={value}>{children}</EnvProviderContext.Provider>;
};

export function useEnv<T>(key: string): T | undefined;
export function useEnv<T>(key: string, defaultValue: T): T;
export function useEnv<T>(key: string, defaultValue?: T): T | undefined {
  const context = useContext(EnvProviderContext);

  if (!context) {
    throw new Error('`useEnv` hook must be used within a `EnvProvider` component');
  }

  const value = context[key];

  if (typeof value !== 'undefined') {
    return value as unknown as T;
  }

  return defaultValue;
}
